import React, { useState } from 'react';
import {
  AppBar, Toolbar, Typography, Button, Box, IconButton,
  Drawer, List, ListItem, ListItemButton, ListItemText, useScrollTrigger,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import ArticleIcon from '@mui/icons-material/Article';

const navLinks = [
  { label: 'About', href: '#about' },
  { label: 'Services', href: '#services' },
  { label: 'Portfolio', href: '#portfolio' },
  { label: 'Shop', href: '#shop' },
  { label: 'Socials', href: '#socials' },
];

export default function Navbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 50 });
  const navigate = useNavigate();
  const location = useLocation();

  const scrollTo = (href: string) => {
    setDrawerOpen(false);
    const hash = href.startsWith('#') ? href : `#${href}`;
    if (location.pathname !== '/') {
      navigate('/' + hash);
    } else {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={trigger ? 4 : 0}
        sx={{
          background: trigger
            ? 'rgba(13, 10, 18, 0.92)'
            : 'transparent',
          backdropFilter: trigger ? 'blur(16px)' : 'none',
          borderBottom: trigger ? '1px solid rgba(255,110,180,0.15)' : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 6 } }}>
          <Typography
            variant="h6"
            onClick={() => navigate('/')}
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #ff6eb4, #b44fff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: 2,
              fontSize: '1.3rem',
              cursor: 'pointer',
            }}
          >
            ✦ panekopanko
          </Typography>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
            {navLinks.map((link) => (
              <Button
                key={link.label}
                onClick={() => scrollTo(link.href)}
                sx={{
                  color: location.pathname === '/' ? 'text.secondary' : 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                  px: 2,
                }}
              >
                {link.label}
              </Button>
            ))}
            <Button
              component={RouterLink}
              to="/shop"
              sx={{
                color: location.pathname === '/shop' ? 'primary.main' : 'text.secondary',
                '&:hover': { color: 'primary.main' },
                px: 2,
              }}
            >
              Shop
            </Button>
            <Button
              component={RouterLink}
              to="/resources"
              sx={{
                color: location.pathname === '/resources' ? 'primary.main' : 'text.secondary',
                '&:hover': { color: 'primary.main' },
                px: 2,
              }}
            >
              Resources
            </Button>
            <Button
              component="a"
              href="https://vtubermodel.wiki"
              startIcon={<ArticleIcon sx={{ fontSize: '16px !important' }} />}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
                px: 2,
              }}
            >
              Docs
            </Button>
          </Box>

          <IconButton
            sx={{ display: { xs: 'flex', md: 'none' }, color: 'primary.main' }}
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 260,
            background: '#160f20',
            borderLeft: '1px solid rgba(255,110,180,0.2)',
            pt: 2,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pb: 2 }}>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'primary.main' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          {navLinks.map((link) => (
            <ListItem key={link.label} disablePadding>
              <ListItemButton onClick={() => scrollTo(link.href)}>
                <ListItemText
                  primary={link.label}
                  primaryTypographyProps={{ sx: { color: 'text.primary', fontWeight: 500 } }}
                />
              </ListItemButton>
            </ListItem>
          ))}
          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/shop" onClick={() => setDrawerOpen(false)}>
              <ListItemText
                primary="Shop"
                primaryTypographyProps={{ sx: { color: 'primary.main', fontWeight: 500 } }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/resources" onClick={() => setDrawerOpen(false)}>
              <ListItemText
                primary="Resources"
                primaryTypographyProps={{ sx: { color: 'primary.main', fontWeight: 500 } }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton component="a" href="/docs/" onClick={() => setDrawerOpen(false)}>
              <ListItemText
                primary="Docs"
                primaryTypographyProps={{ sx: { color: 'primary.main', fontWeight: 500 } }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
    </>
  );
}
